import type { QrStyle, StyleIssue } from './types';

/** Reglas previas de R13.2 en el navegador (espejo de `validate_style.precheck`) para avisar antes de pedir la vista previa. */
export const MIN_CONTRAST = 3;
export const MAX_LOGO_SIZE = 0.22;

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

export function formatRatio(ratio: number): string {
  return `${ratio.toLocaleString('es-CL', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}:1`;
}

export function localPrecheck(style: QrStyle): StyleIssue[] {
  const issues: StyleIssue[] = [];
  const bg = style.color.bg;
  if (style.logo && style.logo.size > MAX_LOGO_SIZE + 1e-9) {
    issues.push({
      code: 'logo_too_large',
      field: 'logo.size',
      message: `El logo ocupa ${Math.round(style.logo.size * 100)} % del ancho; el máximo es 22 %.`,
    });
  }
  const inks: [string, string][] =
    style.color.gradient.type === 'none'
      ? [['color.fg', style.color.fg]]
      : [
          ['color.gradient.from', style.color.gradient.from],
          ['color.gradient.to', style.color.gradient.to],
        ];
  if (style.color.eyeColor) inks.push(['color.eyeColor', style.color.eyeColor]);
  for (const [field, ink] of inks) {
    if (luminance(ink) >= luminance(bg)) {
      issues.push({ code: 'inverted', field, message: 'El fondo tiene que ser más claro que los puntos para que los celulares lo lean.' });
      continue;
    }
    const ratio = contrastRatio(ink, bg);
    if (ratio < MIN_CONTRAST) {
      issues.push({
        code: 'low_contrast',
        field,
        message: `Contraste ${formatRatio(ratio)} entre puntos y fondo; se necesita al menos 3:1. Prueba un color de puntos más oscuro.`,
      });
    }
  }
  return issues;
}
